// ** react imports
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// ** utils imports
import Transition from '../../../utils/Transition';

function CustomerFilterButton({
  align = 'right',
  onStatusFilter,
  onSearchFilter,
  onReset,
  currentFilters = {},
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    search: currentFilters.search || '',
    isActive: currentFilters.isActive,
    isEmailVerified: currentFilters.isEmailVerified,
    isPasswordSet: currentFilters.isPasswordSet,
  });

  const trigger = useRef(null);
  const dropdown = useRef(null);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [dropdownOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [dropdownOpen]);

  // Sync local filters with props
  useEffect(() => {
    setLocalFilters({
      search: currentFilters.search || '',
      isActive: currentFilters.isActive,
      isEmailVerified: currentFilters.isEmailVerified,
      isPasswordSet: currentFilters.isPasswordSet,
    });
  }, [currentFilters]);

  // Check if there are active filters
  const hasActiveFilters =
    localFilters.search.trim() !== '' ||
    localFilters.isEmailVerified !== undefined ||
    localFilters.isPasswordSet !== undefined ||
    localFilters.isActive !== true;

  // Handle filter changes
  const handleSearchChange = e => {
    setLocalFilters(prev => ({
      ...prev,
      search: e.target.value,
    }));
  };

  const handleStatusChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Apply filters
  const handleApply = () => {
    if (onSearchFilter) {
      onSearchFilter(localFilters.search.trim());
    }
    if (onStatusFilter) {
      onStatusFilter({
        isActive: localFilters.isActive,
        isEmailVerified: localFilters.isEmailVerified,
        isPasswordSet: localFilters.isPasswordSet,
      });
    }
    setDropdownOpen(false);
  };

  // Clear all filters
  const handleClear = () => {
    setLocalFilters({
      search: '',
      isActive: true,
      isEmailVerified: undefined,
      isPasswordSet: undefined,
    });

    if (onReset) {
      onReset();
    }
    setDropdownOpen(false);
  };

  return (
    <div className="relative inline-flex z-20">
      <button
        ref={trigger}
        className={`btn px-2.5 bg-white dark:bg-gray-800 border-gray-200 hover:border-gray-300 dark:border-gray-700/60 dark:hover:border-gray-600 text-gray-400 dark:text-gray-500 relative ${
          hasActiveFilters
            ? 'border-violet-300 dark:border-violet-600 bg-violet-50 dark:bg-violet-900/20'
            : ''
        }`}
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
        aria-label="Filter customers"
      >
        <span className="sr-only">Filter</span>
        <svg
          className="fill-current"
          width="16"
          height="16"
          viewBox="0 0 16 16"
        >
          <path d="M0 3a1 1 0 0 1 1-1h14a1 1 0 1 1 0 2H1a1 1 0 0 1-1-1ZM3 8a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1ZM7 12a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2H7Z" />
        </svg>

        {/* Active filter indicator */}
        {hasActiveFilters && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-violet-500 rounded-full"></div>
        )}
      </button>
      <Transition
        show={dropdownOpen}
        tag="div"
        className={`origin-top-right z-50 absolute top-full left-0 right-auto min-w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 pt-1.5 rounded-lg shadow-lg overflow-hidden mt-1 ${
          align === 'right'
            ? 'md:left-auto md:right-0'
            : 'md:left-0 md:right-auto'
        } ${dropdownOpen ? 'block' : 'hidden'}`}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        unmountOnExit={true} // Add this prop
      >
        <div ref={dropdown}>
          <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase pt-1.5 pb-2 px-3">
            Filters
          </div>

          {/* Search Filter */}
          <div className="px-3 pb-3">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Search
            </label>
            <input
              type="text"
              className="w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:border-violet-500 dark:focus:border-violet-400 focus:outline-none"
              placeholder="Search by name, email, ID..."
              value={localFilters.search}
              onChange={handleSearchChange}
            />
          </div>

          {/* Status Filters */}
          <div className="px-3 pb-3">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Account Status
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isActive"
                  checked={localFilters.isActive === true}
                  onChange={() => handleStatusChange('isActive', true)}
                  className="form-radio text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium ml-2 text-gray-700 dark:text-gray-300">
                  Active Only
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isActive"
                  checked={localFilters.isActive === false}
                  onChange={() => handleStatusChange('isActive', false)}
                  className="form-radio text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium ml-2 text-gray-700 dark:text-gray-300">
                  Inactive Only
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isActive"
                  checked={localFilters.isActive === undefined}
                  onChange={() => handleStatusChange('isActive', undefined)}
                  className="form-radio text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium ml-2 text-gray-700 dark:text-gray-300">
                  All Statuses
                </span>
              </label>
            </div>
          </div>

          {/* Verification Filters */}
          <div className="px-3 pb-4">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Verification Status
            </label>
            <div className="space-y-1">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={localFilters.isEmailVerified === true}
                  onChange={e =>
                    handleStatusChange(
                      'isEmailVerified',
                      e.target.checked ? true : undefined
                    )
                  }
                  className="form-checkbox text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium ml-2 text-gray-700 dark:text-gray-300">
                  Email Verified Only
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={localFilters.isPasswordSet === true}
                  onChange={e =>
                    handleStatusChange(
                      'isPasswordSet',
                      e.target.checked ? true : undefined
                    )
                  }
                  className="form-checkbox text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium ml-2 text-gray-700 dark:text-gray-300">
                  Password Set Only
                </span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="py-2 px-3 border-t border-gray-200 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-700/20">
            <div className="flex items-center justify-between">
              <button
                className="btn-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-red-500 hover:text-red-600 uppercase"
                onClick={handleClear}
              >
                Clear All
              </button>
              <button
                className="btn-xs bg-violet-600 text-white hover:bg-violet-700 dark:bg-violet-600 dark:hover:bg-violet-700 uppercase"
                onClick={handleApply}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
}

CustomerFilterButton.propTypes = {
  align: PropTypes.oneOf(['left', 'right']),
  onStatusFilter: PropTypes.func,
  onSearchFilter: PropTypes.func,
  onReset: PropTypes.func,
  currentFilters: PropTypes.object,
};

export default CustomerFilterButton;
